import { useStore } from 'jotai';
import { useRef } from 'react';
import type { BeautyInfo } from 'shared/src/api/getMemberInfo/types';
import { useInstanceId } from 'shared/src/hooks/useInstanceId';
import { formMapAtom, seedMapAtom } from './atoms';

export type MyPersonalizationFormSeed = {
  height: number | null;
  weight: number | null;
  top: number | null;
  bottom: number | null;
  shoes: number | null;
  skinTone: BeautyInfo | null;
  skinType: BeautyInfo | null;
  skinProblems: BeautyInfo[] | null;
  isAlreadyAgreed: boolean;
};

/**
 * 현재 instanceId 슬롯에 form/seed 초기값을 1회 주입.
 *
 * - 같은 instanceId에서 두 번째 호출부터는 no-op (사용자 수정값 보존)
 * - 새 instanceId (= 화면 재진입)면 다시 주입
 * - jotai의 useHydrateAtoms와 같이 render 단계에서 sync set → 자식이 첫 render에서
 *   서버 값으로 보임 (flicker 없음)
 */
export const useMyPersonalizationFormHydration = (
  seed: MyPersonalizationFormSeed,
) => {
  const instanceId = useInstanceId();
  const store = useStore();
  const hydratedRef = useRef<Set<string>>(new Set());

  if (!hydratedRef.current.has(instanceId)) {
    hydratedRef.current.add(instanceId);
    const skinProblems = seed.skinProblems ?? [];
    store.set(formMapAtom, prev => ({
      ...prev,
      [instanceId]: {
        height: seed.height,
        weight: seed.weight,
        top: seed.top,
        bottom: seed.bottom,
        shoes: seed.shoes,
        skinTone: seed.skinTone,
        skinType: seed.skinType,
        skinProblems,
        checksPrivacyAgreement: true,
      },
    }));
    store.set(seedMapAtom, prev => ({
      ...prev,
      [instanceId]: {
        isAlreadyAgreed: seed.isAlreadyAgreed,
        initialSkinProblemSnos: [...skinProblems.map(p => p.sno)].sort(
          (a, b) => a - b,
        ),
      },
    }));
  }
};

